import {
  ApiOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';

import { DoubleThunderbolt } from 'app/components/commons/DoubleThunderbolt/DoubleThunderbolt';
import {
  BABY_COT_BED_CATEGORY_ID,
  GUEST_BED_BED_CATEGORY_ID,
} from 'app/typings';
import type { ExtranetPackage } from 'app/typings/packages';
import { assertNever } from 'app/utils/typing';

import type { BedTaskInformation, Task, TaskInformation } from './Types';
import { TASK_NAMES, TaskName } from './Types';

export const getTaskInformations = (
  taskName: TaskName,
  packageCount: number,
  childrenAllowedCount: number
): TaskInformation | BedTaskInformation => {
  switch (taskName) {
    case TaskName.BabyCot:
      return {
        taskName: TaskName.BabyCot,
        priority: 6,
        categoryId: BABY_COT_BED_CATEGORY_ID,
        title: 'Complete your baby cots',
        description: 'Extend your offer to families coming with babies.',
        icon: <UserAddOutlined />,
        redirect: null,
        max: 1,
        min: 0,
        isMomentAvailable: false,
      };

    case TaskName.GuestBed:
      return {
        taskName: TaskName.GuestBed,
        categoryId: GUEST_BED_BED_CATEGORY_ID,
        priority: 7,
        title: 'Complete your guest beds',
        description: 'Extend your offer to users coming with children.',
        icon: <UserAddOutlined />,
        redirect: null,
        max: 1,
        min: 0,
        isMomentAvailable: false,
      };

    case TaskName.PricingRule:
      return {
        taskName: TaskName.PricingRule,
        priority: 5,
        title: 'Set a standard discount',
        description:
          'Automatically publish your connected stocks according to your rules.',
        icon: <ThunderboltOutlined />,
        redirect: 'inventory',
        max: packageCount,
        min: 0,
        isMomentAvailable: false,
      };

    case TaskName.LastMinutePricingRule:
      return {
        taskName: TaskName.LastMinutePricingRule,
        priority: 4,
        title: 'Set a last minute discount',
        description:
          'Set a different default discount to your last minute stocks.',
        icon: <DoubleThunderbolt />,
        redirect: 'inventory',
        max: packageCount,
        min: 0,
        isMomentAvailable: false,
      };
    case TaskName.AttractExtraGuests:
      return {
        taskName: TaskName.AttractExtraGuests,
        priority: 2,
        title: 'Attract extra guests',
        description:
          'Adapt the price of your package for users travelling with more than 2 adults.',
        icon: <UsergroupAddOutlined />,
        redirect: null,
        max: packageCount,
        min: 0,
        isMomentAvailable: true,
      };
    case TaskName.ConfigureChildPrice:
      return {
        taskName: TaskName.ConfigureChildPrice,
        priority: 2,
        title: 'Configure child price',
        description:
          'Adapt the price of your package for families travelling with children.',
        icon: <UsergroupAddOutlined />,
        redirect: null,
        max: childrenAllowedCount,
        min: 0,
        isMomentAvailable: true,
      };
    case TaskName.AttractSingleGuests:
      return {
        taskName: TaskName.AttractSingleGuests,
        priority: 3,
        title: 'Attract single guests',
        description:
          'Adapt the price of your package for users travelling alone.',
        icon: <UserOutlined />,
        redirect: null,
        max: packageCount,
        min: 0,
        isMomentAvailable: true,
      };

    case TaskName.EarlyCheckin:
      return {
        taskName: TaskName.EarlyCheckin,
        priority: 1,
        title: 'Boost your revenues with an Early Check-in',
        description:
          'Add an optional Early Check-in to improve your page visibility, conversion and average basket.',
        icon: <HistoryOutlined />,
        redirect: null,
        max: 1,
        min: 0,
        isMomentAvailable: false,
      };
    case TaskName.LateCheckout:
      return {
        taskName: TaskName.LateCheckout,
        priority: 1,
        title: 'Boost your revenues with a Late Check-out',
        description:
          'Add an optional Late Check-out to improve your page visibility, conversion and average basket.',
        icon: <HistoryOutlined />,
        redirect: null,
        max: 1,
        min: 0,
        isMomentAvailable: false,
      };
    case TaskName.AutoInventory:
      return {
        taskName: TaskName.AutoInventory,
        priority: 0,
        title: 'Set up Auto inventory',
        description:
          'Try out stock synchronization with other online platforms',
        icon: <ApiOutlined />,
        redirect: 'inventory',
        max: packageCount,
        min: 0,
        isMomentAvailable: false,
      };
    case TaskName.VirtualCards:
      return {
        taskName: TaskName.VirtualCards,
        priority: -1,
        title: 'Configure Virtual Cards',
        description: 'Get paid on the day of check-in.',
        icon: <CreditCardOutlined />,
        redirect: `payment`,
        closeTaskCenter: true,
        max: 1,
        min: 0,
        isMomentAvailable: true,
        buttonLabel: 'Discover',
      };
    default:
      assertNever(taskName);
  }
};

export const getProgressPercent = (actual: number, max: number) => {
  if (max === 0) {
    return 0;
  }

  return (actual / max) * 100;
};

export const createTaskLists = (
  packageCount: number,
  hasBabyCot: boolean,
  hasGuestBed: boolean,
  hasChannelManager: boolean,
  pricingRuleCount: number,
  lastMinutePricingRuleCount: number,
  additionalAdultPriceCount: number,
  singleCustomerDiscountCount: number,
  additionalChildPriceCount: number,
  hasEarlyCheckInExp: boolean,
  hasLateCheckoutExp: boolean,
  autoInventoryPricingRulesCount: number,
  issuingAllowed: boolean,
  issuingActivated: boolean,
  childrenAllowedCount: number,
  isMoment?: boolean
) => {
  const doneTasks: Task[] = [];

  const missingTasks: Task[] = [];

  TASK_NAMES.forEach((taskName) =>
    addTaskWithCompletion(
      taskName,
      packageCount,
      doneTasks,
      missingTasks,
      hasBabyCot,
      hasGuestBed,
      hasChannelManager,
      pricingRuleCount,
      lastMinutePricingRuleCount,
      additionalAdultPriceCount,
      singleCustomerDiscountCount,
      additionalChildPriceCount,
      hasEarlyCheckInExp,
      hasLateCheckoutExp,
      autoInventoryPricingRulesCount,
      issuingAllowed,
      issuingActivated,
      childrenAllowedCount,
      isMoment
    )
  );

  return { doneTasks, missingTasks };
};

const computeTaskCompletion = (
  taskInformations: TaskInformation | BedTaskInformation,
  value: number
): Task => {
  return {
    ...taskInformations,
    value,
    percent: getProgressPercent(value, taskInformations.max),
  };
};

const addTaskWithCompletion = (
  taskName: TaskName,
  packageCount: number,
  doneTasksInformations: Task[],
  missingTaskInformations: Task[],
  hasBabyCot: boolean,
  hasGuestBed: boolean,
  hasChannelManager: boolean,
  pricingRuleCount: number,
  lastMinutePricingRuleCount: number,
  additionalAdultPriceCount: number,
  singleCustomerDiscountCount: number,
  additionalChildPriceCount: number,
  hasEarlyCheckInExp: boolean,
  hasLateCheckoutExp: boolean,
  autoInventoryPricingRulesCount: number,
  issuingAllowed: boolean,
  issuingActivated: boolean,
  childrenAllowedCount: number,
  isMoment?: boolean
) => {
  const taskInformations = getTaskInformations(
    taskName,
    packageCount,
    childrenAllowedCount
  );

  if (isMoment && !taskInformations.isMomentAvailable) {
    return;
  }

  switch (taskName) {
    case TaskName.BabyCot:
      const babyCotTask = computeTaskCompletion(
        taskInformations,
        hasBabyCot ? taskInformations.max : taskInformations.min
      );

      hasBabyCot
        ? doneTasksInformations.push(babyCotTask)
        : missingTaskInformations.push(babyCotTask);

      break;
    case TaskName.GuestBed:
      const guestBedTask = computeTaskCompletion(
        taskInformations,
        hasGuestBed ? taskInformations.max : taskInformations.min
      );

      hasGuestBed
        ? doneTasksInformations.push(guestBedTask)
        : missingTaskInformations.push(guestBedTask);

      break;
    case TaskName.PricingRule:
      const pricingRuleTask = computeTaskCompletion(
        taskInformations,
        pricingRuleCount
      );

      hasChannelManager &&
        (pricingRuleCount === taskInformations.max
          ? doneTasksInformations.push(pricingRuleTask)
          : missingTaskInformations.push(pricingRuleTask));

      break;
    case TaskName.LastMinutePricingRule:
      const lastMinutePricingRuleTask = computeTaskCompletion(
        taskInformations,
        lastMinutePricingRuleCount
      );

      hasChannelManager &&
        (lastMinutePricingRuleCount === taskInformations.max
          ? doneTasksInformations.push(lastMinutePricingRuleTask)
          : missingTaskInformations.push(lastMinutePricingRuleTask));

      break;
    case TaskName.AttractExtraGuests:
      const attractExtraGuestsTask = computeTaskCompletion(
        taskInformations,
        additionalAdultPriceCount
      );

      additionalAdultPriceCount === taskInformations.max
        ? doneTasksInformations.push(attractExtraGuestsTask)
        : missingTaskInformations.push(attractExtraGuestsTask);

      break;
    case TaskName.AttractSingleGuests:
      const attractSingleGuestsTask = computeTaskCompletion(
        taskInformations,
        singleCustomerDiscountCount
      );

      singleCustomerDiscountCount === taskInformations.max
        ? doneTasksInformations.push(attractSingleGuestsTask)
        : missingTaskInformations.push(attractSingleGuestsTask);

      break;
    case TaskName.ConfigureChildPrice:
      const configureChildPriceTask = computeTaskCompletion(
        taskInformations,
        additionalChildPriceCount
      );

      childrenAllowedCount > 0 &&
        (additionalChildPriceCount === taskInformations.max
          ? doneTasksInformations.push(configureChildPriceTask)
          : missingTaskInformations.push(configureChildPriceTask));

      break;
    case TaskName.EarlyCheckin:
      const earlyCheckinTask = computeTaskCompletion(
        taskInformations,
        hasEarlyCheckInExp ? taskInformations.max : taskInformations.min
      );

      !hasEarlyCheckInExp && missingTaskInformations.push(earlyCheckinTask);

      break;
    case TaskName.LateCheckout:
      const lateCheckoutTask = computeTaskCompletion(
        taskInformations,
        hasLateCheckoutExp ? taskInformations.max : taskInformations.min
      );

      !hasLateCheckoutExp && missingTaskInformations.push(lateCheckoutTask);

      break;
    case TaskName.AutoInventory:
      const autoInventoryTask = computeTaskCompletion(
        taskInformations,
        autoInventoryPricingRulesCount
      );

      !hasChannelManager &&
        (autoInventoryPricingRulesCount === taskInformations.max
          ? doneTasksInformations.push(autoInventoryTask)
          : missingTaskInformations.push(autoInventoryTask));

      break;
    case TaskName.VirtualCards:
      const virtualCardsTask = computeTaskCompletion(
        taskInformations,
        issuingActivated ? taskInformations.max : taskInformations.min
      );

      issuingAllowed &&
        (issuingActivated
          ? doneTasksInformations.push(virtualCardsTask)
          : missingTaskInformations.push(virtualCardsTask));

      break;
    default:
      assertNever(taskName);
  }
};

export const sortTasks = (
  doneTasksInformations: Task[],
  missingTaskInformations: Task[]
) => {
  const doneTasks = doneTasksInformations.sort(
    (taskA, taskB) => taskA.priority - taskB.priority
  );

  const missingTasks = missingTaskInformations.sort(
    (taskA, taskB) => taskA.priority - taskB.priority
  );

  return { doneTasks, missingTasks };
};

export const isDoneTask = (taskName: TaskName, doneTasks: Task[]) =>
  doneTasks.map((task) => task.taskName).includes(taskName);

export const getPackageHasAttributeCount = (
  packages: ExtranetPackage[] | undefined,
  attibrute: keyof ExtranetPackage
) => packages?.filter((pkg) => pkg[attibrute] !== undefined).length ?? 0;
